/**
 * FilterBar — horizontal filter strip below the asset grid toolbar.
 *
 * Filter chips for editorial_status, priority, asset_type, tags.
 * Active filters shown as removable pills. "Save as Collection" button.
 */

import { useState } from 'react'
import { X, Bookmark, ChevronDown } from 'lucide-react'
import type { EditorialStatus, Priority } from '../../types/fileManager'

import { type ActiveFilters, EMPTY_FILTERS, hasActiveFilters } from './filters'
export type { ActiveFilters } from './filters'

interface FilterBarProps {
  filters: ActiveFilters
  onChange: (filters: ActiveFilters) => void
  onSaveAsCollection?: () => void
  allTags?: string[]
}

const STATUS_OPTIONS: { value: EditorialStatus; label: string }[] = [
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'selects', label: 'Selects' },
  { value: 'rejected', label: 'Rejected' },
]

const PRIORITY_OPTIONS: { value: Priority; label: string }[] = [
  { value: 'p1', label: 'P1' },
  { value: 'p2', label: 'P2' },
  { value: 'p3', label: 'P3' },
]

const TYPE_OPTIONS = [
  { value: 'footage', label: 'Footage' },
  { value: 'inspiration', label: 'Inspiration' },
  { value: 'generated', label: 'Generated' },
]

function FilterDropdown({ label, options, selected, onToggle }: {
  label: string
  options: { value: string; label: string }[]
  selected: string[]
  onToggle: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  const activeCount = selected.length

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`fm-filter-chip flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg transition-colors ${
          activeCount > 0
            ? 'bg-[var(--rq-blue)]/15 text-[var(--rq-blue)] border border-[var(--rq-blue)]/30'
            : 'bg-white/5 text-[var(--rq-text-soft)] border border-white/10 hover:border-white/20'
        }`}
      >
        {label}{activeCount > 0 ? ` (${activeCount})` : ''}
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-30 bg-[var(--rq-surface)] border border-white/15 rounded-lg shadow-lg py-1 min-w-[140px]">
            {options.map(o => (
              <button
                key={o.value}
                onClick={() => onToggle(o.value)}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
              >
                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                  selected.includes(o.value) ? 'bg-[var(--rq-blue)] border-[var(--rq-blue)]' : 'border-white/20'
                }`}>
                  {selected.includes(o.value) && <span className="text-white text-[8px]">✓</span>}
                </div>
                <span className={selected.includes(o.value) ? 'text-[var(--rq-text)]' : 'text-[var(--rq-text-soft)]'}>
                  {o.label}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function TagFilterInput({ tags, selected, onToggle }: {
  tags: string[]
  selected: string[]
  onToggle: (tag: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const activeCount = selected.length

  const filtered = tags.filter(
    t => t.toLowerCase().includes(input.toLowerCase()),
  ).slice(0, 10)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`fm-filter-chip flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg transition-colors ${
          activeCount > 0
            ? 'bg-[var(--rq-blue)]/15 text-[var(--rq-blue)] border border-[var(--rq-blue)]/30'
            : 'bg-white/5 text-[var(--rq-text-soft)] border border-white/10 hover:border-white/20'
        }`}
      >
        Tags{activeCount > 0 ? ` (${activeCount})` : ''}
        <ChevronDown className="w-3 h-3" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full mt-1 z-30 bg-[var(--rq-surface)] border border-white/15 rounded-lg shadow-lg py-1 min-w-[160px]">
            <div className="px-2 pb-1">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Search tags..."
                className="w-full text-xs bg-transparent border-b border-white/10 py-1 outline-none text-[var(--rq-text)] placeholder:text-[var(--rq-text-soft)]/50"
                autoFocus
              />
            </div>
            {filtered.length === 0 && (
              <div className="px-3 py-1.5 text-xs text-[var(--rq-text-soft)]">No tags found</div>
            )}
            {filtered.map(tag => (
              <button
                key={tag}
                onClick={() => onToggle(tag)}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
              >
                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                  selected.includes(tag) ? 'bg-[var(--rq-blue)] border-[var(--rq-blue)]' : 'border-white/20'
                }`}>
                  {selected.includes(tag) && <span className="text-white text-[8px]">✓</span>}
                </div>
                <span className={selected.includes(tag) ? 'text-[var(--rq-text)]' : 'text-[var(--rq-text-soft)]'}>
                  {tag}
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default function FilterBar({ filters, onChange, onSaveAsCollection, allTags = [] }: FilterBarProps) {
  const active = hasActiveFilters(filters)

  const toggleValue = (key: keyof ActiveFilters, value: string) => {
    const current = filters[key]
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value]
    onChange({ ...filters, [key]: next })
  }

  const removeFilter = (key: keyof ActiveFilters, value: string) => {
    onChange({ ...filters, [key]: filters[key].filter(v => v !== value) })
  }

  const clearAll = () => onChange(EMPTY_FILTERS)

  return (
    <div className="fm-filter-bar border-b border-white/10 px-4 py-2">
      {/* Filter dropdowns */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-[var(--rq-text-soft)] mr-1">Filters</span>

        <FilterDropdown
          label="Status"
          options={STATUS_OPTIONS}
          selected={filters.editorial_status}
          onToggle={v => toggleValue('editorial_status', v)}
        />
        <FilterDropdown
          label="Priority"
          options={PRIORITY_OPTIONS}
          selected={filters.priority}
          onToggle={v => toggleValue('priority', v)}
        />
        <FilterDropdown
          label="Type"
          options={TYPE_OPTIONS}
          selected={filters.asset_type}
          onToggle={v => toggleValue('asset_type', v)}
        />
        <TagFilterInput
          tags={allTags}
          selected={filters.tags}
          onToggle={v => toggleValue('tags', v)}
        />

        {active && (
          <>
            <button
              onClick={clearAll}
              className="text-xs text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors ml-1"
            >
              Clear all
            </button>
            {onSaveAsCollection && (
              <button
                onClick={onSaveAsCollection}
                className="flex items-center gap-1 text-xs text-[var(--rq-blue)] hover:text-blue-300 transition-colors ml-1"
              >
                <Bookmark className="w-3 h-3" />
                Save as Collection
              </button>
            )}
          </>
        )}
      </div>

      {/* Active filter pills */}
      {active && (
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          {filters.editorial_status.map(s => (
            <span key={`s-${s}`} className="fm-tag-pill flex items-center gap-1 px-2 py-0.5 text-[10px]">
              {s.replace('_', ' ')}
              <button onClick={() => removeFilter('editorial_status', s)}><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
          {filters.priority.map(p => (
            <span key={`p-${p}`} className="fm-tag-pill flex items-center gap-1 px-2 py-0.5 text-[10px]">
              {p.toUpperCase()}
              <button onClick={() => removeFilter('priority', p)}><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
          {filters.asset_type.map(t => (
            <span key={`t-${t}`} className="fm-tag-pill flex items-center gap-1 px-2 py-0.5 text-[10px]">
              {t}
              <button onClick={() => removeFilter('asset_type', t)}><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
          {filters.tags.map(tag => (
            <span key={`tag-${tag}`} className="fm-tag-pill flex items-center gap-1 px-2 py-0.5 text-[10px]">
              #{tag}
              <button onClick={() => removeFilter('tags', tag)}><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
